import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLineUp } from '@fortawesome/pro-light-svg-icons/faChartLineUp';
import { faShieldCheck } from '@fortawesome/pro-light-svg-icons/faShieldCheck';
import {
  OUTREACH_HEALTH_MAX_BOUNCE_RATE,
  OUTREACH_RAMP,
  OUTREACH_WARMUP_DAYS,
} from 'fym-shared/src/music24/outreach/mailboxRamp';
import { TypedMemo } from '../../../common/types';
import { useMusic24Capabilities } from '../../../music24/hooks/useMusic24Capabilities';
import { mailboxMessages as messages } from './mailboxMessages';
import { rampEnds } from './mailboxLines';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Mailboxes card; each part uses its own slice of it */
import styles from './Mailboxes.module.scss';

/**
 * The two facts under the rows (design outreach-v2 q1): why a mailbox starts slowly, with the ramp
 * steps as chips, and when we pause one. The numbers come from fym-shared `mailboxRamp.ts`. The
 * slow fact also names the new pitches a day of the plan (`outreachPitchesPerDay`, per artist), so
 * the cap of a mailbox does not read as the pitch number (QA 2026-10-28 skp-15).
 */
const MailboxFactsComponent: React.FC = () => {
  const intl = useIntl();
  const { first, max } = rampEnds();
  const { outreachPitchesPerDay } = useMusic24Capabilities();
  return (
    <div className={styles.facts} data-testid="mailbox-facts">
      <div className={styles.fact}>
        <span className={styles.factIcon} aria-hidden="true">
          <FontAwesomeIcon icon={faChartLineUp} />
        </span>
        <div>
          <b>{intl.formatMessage(messages.slowTitle)}</b>
          <p>
            {intl.formatMessage(messages.slowBody, {
              first,
              max,
              weeks: Math.round(OUTREACH_WARMUP_DAYS / 7),
            })}
          </p>
          <p data-testid="mailbox-plan-rate">
            {intl.formatMessage(messages.slowPlanRate, { rate: outreachPitchesPerDay })}
          </p>
          <div className={styles.chips}>
            {OUTREACH_RAMP.map((step, index) => (
              <span key={step.fromDay} className={clsx(index === 0 && styles.chipOn)}>
                {index === OUTREACH_RAMP.length - 1
                  ? intl.formatMessage(messages.slowLastChip, { cap: step.perDay })
                  : step.perDay}
              </span>
            ))}
          </div>
        </div>
      </div>
      <div className={styles.fact}>
        <span className={styles.factIcon} aria-hidden="true">
          <FontAwesomeIcon icon={faShieldCheck} />
        </span>
        <div>
          <b>{intl.formatMessage(messages.pauseTitle)}</b>
          <p>{intl.formatMessage(messages.pauseBody, { bounce: Math.round(OUTREACH_HEALTH_MAX_BOUNCE_RATE * 100) })}</p>
        </div>
      </div>
    </div>
  );
};

export const MailboxFacts = TypedMemo(MailboxFactsComponent);
